// frontend/src/components/ExpressionEditor/OperandDropZone.tsx
import React from 'react';
import { useDroppable } from '@dnd-kit/core';
import { functionRegistry } from '../../utils/functionRegistry';

interface OperandDropZoneProps {
  id: string;
  child?: React.ReactNode;
  onRemove?: () => void;
  label?: string;
  onClick?: () => void;
  functionId?: string;
  existingTypes?: string[];
  isExtraSlot?: boolean;
  isHovered?: boolean;
}

const OperandDropZone: React.FC<OperandDropZoneProps> = ({
  id,
  child,
  onRemove,
  label,
  onClick,
  functionId,
  existingTypes = [],
  isExtraSlot,
  isHovered,
}) => {
  const { setNodeRef, isOver, active } = useDroppable({
    id,
    data: { type: 'expression-slot' },
  });

  // ─────────────────────────────────────────────────────────────────────
  // Fixed: Correctly resolve the return type of whatever is being dragged
  // ─────────────────────────────────────────────────────────────────────
  const draggedType = React.useMemo(() => {
    const data = active?.data.current;
    if (!data) return undefined;

    // 1. Series blocks are always numbers <= Series could also be a date
    // removing this code for now...we will have to consider how we want
    // to handle this later.
    //if (data.type === 'series') return 'number';

    // 2. Constant dragged from the Constants palette
    if (data.type === 'constant') {
      return (data.constantType ?? 'number') as string;
    }

    // 3. New function dragged from the AvailableFunctions palette
    if (data.type === 'function' && data.functionId) {
      const def = functionRegistry.get(data.functionId);
      return def?.returnType ?? 'number'; // fallback for any missing definition
    }

    // 4. Existing expression block being moved around on the canvas
    //    (these already attach their returnType to the drag payload)
    if (data.returnType) {
      return data.returnType as string;
    }

    // Fallback – unknown
    return undefined;
  }, [active?.data.current]);

  let isValid = true;
  let tooltip = '';

  if (functionId && draggedType && isOver) {
    const validator = (functionRegistry.get(functionId) as { validateOperand?: (a: string[], b: string) => boolean } | undefined)?.validateOperand;
    if (validator) {
      isValid = validator(existingTypes, draggedType);
      tooltip = isValid ? '' : `Requires ${existingTypes.length ? 'same type as existing' : 'number or string'}`;
    } else {
      // If no custom validator, be permissive (shouldn't happen for built-in functions)
      isValid = true;
    }
  }

  const isEmpty = !child;

  return (
    <div
      id={id}
      ref={setNodeRef}
      data-testid="expression-slot"
      className={`
        min-w-[120px] max-w-full min-h-[40px] overflow-visible flex items-center justify-between
        border-2 rounded p-2 transition-all duration-150
        ${isExtraSlot
          ? isOver && isValid
            ? 'border-ink bg-table-win shadow-lg'
            : isOver && !isValid
              ? 'border-error bg-table-loss animate-pulse animate-shake ring-2 ring-error'
              : 'border-dashed border-rule bg-newsprint hover:border-ink hover:bg-newsprint-dark'
          : isOver
            ? isValid
              ? 'border-ink bg-table-win'
              : 'border-error bg-table-loss animate-pulse animate-shake ring-2 ring-error'
            : isHovered
              ? 'border-ink bg-newsprint-dark'
              : 'border-rule bg-newsprint-dark'
        }
      `}
      onClick={isEmpty && !isExtraSlot ? onClick : undefined}
      title={tooltip}
    >
      <div className="flex-1">
        {child ?? (
          <span className={`text-sm font-bold cursor-pointer ${isExtraSlot ? 'text-ink/60' : 'text-ink'}`}>
            {isExtraSlot ? '+ Add argument' : (label ?? 'Drop series or enter value here')}
          </span>
        )}
      </div>
      {child && onRemove && !isExtraSlot && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className="ml-2 text-error text-xs hover:underline"
        >
          ×
        </button>
      )}
    </div>
  );
};

export default OperandDropZone;